/**
 * Product instrumentation (M1.16.3, M1.17.2, M1.17.3, M1.18.2).
 *
 * Four hypotheses are settled by counting: whether people come back (H1),
 * whether keyboard triage is fast (H6), whether Inbox mode is adopted (H8),
 * and whether the timeline has a bottom people reach (H9).
 *
 * ## What this deliberately does not measure
 *
 * Not session length, not time in app. PRD §9 makes both non-targets, and an
 * instrument that exists gets optimised against eventually — so the way to not
 * optimise for engagement is to not collect it. `session_end` carries no
 * duration. What it carries is whether the session ended having **reached the
 * end of the list**, which is the product's actual claim.
 *
 * Nothing identifying an item, a feed or a person is sent. The server takes an
 * allowlist of names and property values and rejects everything else, so this
 * and that are two halves of one rule.
 *
 * ## Batched, and flushed when the page goes away
 *
 * Events are queued and sent together, because a keyboard-triage session
 * produces one per keystroke. The flush runs on `pagehide` — not `unload`,
 * which mobile browsers do not fire, and not `beforeunload`, which blocks the
 * navigation — and the request is sent with `keepalive`, so the browser
 * finishes it after the page is gone. `sendBeacon` would do the same but
 * cannot carry an `Authorization` header, and the endpoint is authenticated.
 */

export type EventName =
  | 'session_start'
  | 'session_end'
  | 'bottom_reached'
  | 'session_abandoned_scrolling'
  | 'triage_complete'
  | 'action_performed'
  | 'inbox_mode_chosen'
  | 'inbox_mode_changed';

export interface TelemetryEvent {
  readonly name: EventName;
  readonly properties?: Record<string, number | boolean | string>;
}

export interface TelemetryOptions {
  readonly send: (events: readonly TelemetryEvent[]) => void | Promise<void>;
  /** Flush when this many are queued. */
  readonly batchSize?: number;
  readonly now?: () => number;
}

export class Telemetry {
  readonly #options: TelemetryOptions;
  #queue: TelemetryEvent[] = [];

  /* --- the H6 and H9 counters, kept here so no component has to own them --- */

  #keyboardActions = 0;
  #pointerActions = 0;
  #sessionStartedAt: number;
  #reachedEnd = false;
  #scrolled = false;

  constructor(options: TelemetryOptions) {
    this.#options = options;
    this.#sessionStartedAt = (options.now ?? Date.now)();
  }

  record(event: TelemetryEvent): void {
    this.#queue.push(event);
    if (this.#queue.length >= (this.#options.batchSize ?? 10)) {
      this.flush();
    }
  }

  /** H1. Recorded per account; the return-rate calculation is the server's. */
  sessionStart(): void {
    this.record({ name: 'session_start' });
  }

  /** H6 — which input people actually triage with. */
  action(kind: 'read' | 'unread' | 'star' | 'mark_all_read' | 'open', via: 'keyboard' | 'pointer'): void {
    if (via === 'keyboard') {
      this.#keyboardActions += 1;
    } else {
      this.#pointerActions += 1;
    }
    this.record({ name: 'action_performed', properties: { action: kind, via } });
  }

  /** H9. The in-scope set was exhausted — the timeline has a bottom. */
  reachedEnd(itemsRead: number): void {
    if (this.#reachedEnd) {
      return;
    }
    this.#reachedEnd = true;
    this.record({ name: 'bottom_reached', properties: { items_read: itemsRead } });
  }

  /** Someone scrolled. Only interesting for telling "gave up" from "finished". */
  scrolled(): void {
    this.#scrolled = true;
  }

  /**
   * H6 — time to zero.
   *
   * Emitted when a scope reaches zero unread, with the share of actions taken
   * by keyboard. Time is measured from the start of the session rather than of
   * the triage, because a reader does not announce when they begin.
   */
  triageComplete(seconds: number): void {
    const total = this.#keyboardActions + this.#pointerActions;
    this.record({
      name: 'triage_complete',
      properties: {
        seconds: Math.round(seconds),
        actions: total,
        keyboard_share: total === 0 ? 0 : Math.round((this.#keyboardActions / total) * 100),
      },
    });
  }

  /**
   * The end of a session (M1.16.3's third criterion).
   *
   * "Reached the end" and "gave up scrolling" are recorded as different
   * events, because they mean opposite things about the product's central
   * claim and a single "session_end" would conflate them.
   */
  sessionEnd(): void {
    if (this.#reachedEnd) {
      this.record({ name: 'session_end', properties: { exhausted: true } });
    } else if (this.#scrolled) {
      this.record({ name: 'session_abandoned_scrolling' });
      this.record({ name: 'session_end', properties: { exhausted: false } });
    } else {
      this.record({ name: 'session_end', properties: { exhausted: false } });
    }
    this.flush();
  }

  /** H8 — D18's default was a strategic bet; this is what tests it. */
  inboxMode(enabled: boolean, where: 'onboarding' | 'settings'): void {
    this.record({
      name: where === 'onboarding' ? 'inbox_mode_chosen' : 'inbox_mode_changed',
      properties: { mode: enabled ? 'inbox' : 'quiet', where },
    });
  }

  flush(): void {
    if (this.#queue.length === 0) {
      return;
    }
    const batch = this.#queue;
    this.#queue = [];
    void this.#options.send(batch);
  }

  /** Visible for tests. */
  get pending(): readonly TelemetryEvent[] {
    return this.#queue;
  }

  get sessionStartedAt(): number {
    return this.#sessionStartedAt;
  }
}

/**
 * The transport: fire and forget.
 *
 * Errors are swallowed here rather than in each caller. A reader must never
 * see a failure because a metric could not be recorded, and a telemetry
 * pipeline that can break the product is worse than no telemetry.
 */
export function browserTransport(
  post: (events: readonly TelemetryEvent[]) => Promise<void>,
): (events: readonly TelemetryEvent[]) => void {
  return (events) => {
    void post(events).catch(() => {
      // A reader must never see an error because a metric failed to send.
    });
  };
}
